import api from './api';

/**
 * Investor Service
 * Handles all investor-specific API calls
 */
export const investorService = {
  // ==================== Dashboard ====================
  
  /**
   * Get comprehensive investor dashboard data
   */
  async getDashboard() {
    return api.get('/investor/dashboard');
  },

  /**
   * Get dashboard widgets
   */
  async getDashboardWidgets() {
    return api.get('/investor/dashboard/widgets');
  },

  /**
   * Save dashboard layout preferences
   */
  async saveDashboardLayout(layout) {
    return api.post('/investor/dashboard/layout', layout);
  },

  // ==================== Profile ====================
  
  /**
   * Get investor profile
   */
  async getProfile(userId) {
    return api.get(`/investor/profile/${userId}`);
  },

  /**
   * Update investor profile
   */
  async updateProfile(profileData) {
    return api.put('/investor/profile', profileData);
  },

  /**
   * Update investment preferences
   */
  async updateInvestmentPreferences(preferences) {
    return api.put('/investor/profile/investment-preferences', preferences);
  },

  /**
   * Get activity history
   */
  async getActivityHistory(params) {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/investor/profile/activity?${queryString}`);
  },

  // ==================== Portfolio ====================
  
  /**
   * Get portfolio summary
   */
  async getPortfolioSummary() {
    return api.get('/investor/portfolio/summary');
  },

  /**
   * Get asset allocation
   */
  async getAssetAllocation() {
    return api.get('/investor/portfolio/allocation');
  },

  /**
   * Get performance metrics
   */
  async getPerformanceMetrics(params) {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/investor/portfolio/performance?${queryString}`);
  },

  /**
   * Get all holdings
   */
  async getHoldings() {
    return api.get('/investor/portfolio/holdings');
  },

  /**
   * Get top performing holdings
   */
  async getTopPerformers(limit = 10) {
    return api.get(`/investor/portfolio/top-performers?limit=${limit}`);
  },

  // ==================== Recommendations ====================
  
  /**
   * Get all recommendations
   */
  async getRecommendations(params) {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/investor/recommendations?${queryString}`);
  },

  /**
   * Get recommendation details
   */
  async getRecommendationDetails(id) {
    return api.get(`/investor/recommendations/${id}`);
  },

  /**
   * Accept recommendation
   */
  async acceptRecommendation(id, feedback) {
    return api.post(`/investor/recommendations/${id}/accept`, feedback);
  },

  /**
   * Reject recommendation
   */
  async rejectRecommendation(id, reason) {
    return api.post(`/investor/recommendations/${id}/reject`, { reason });
  },

  /**
   * Provide feedback on recommendation
   */
  async provideFeedback(id, feedback) {
    return api.post(`/investor/recommendations/${id}/feedback`, feedback);
  },

  // ==================== Professional Connections ====================
  
  /**
   * Get connected analysts
   */
  async getConnectedAnalysts() {
    return api.get('/investor/analysts');
  },

  /**
   * Get connected advisors
   */
  async getConnectedAdvisors() {
    return api.get('/investor/advisors');
  },

  /**
   * Connect with analyst
   */
  async connectWithAnalyst(analystId, message) {
    return api.post(`/investor/analysts/${analystId}/connect`, { message });
  },

  /**
   * Connect with advisor
   */
  async connectWithAdvisor(advisorId, message) {
    return api.post(`/investor/advisors/${advisorId}/connect`, { message });
  },

  // ==================== Research & Reports ====================
  
  /**
   * Get research reports
   */
  async getResearchReports(params) {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/investor/research-reports?${queryString}`);
  },

  /**
   * Get research report details
   */
  async getResearchReportDetails(id) {
    return api.get(`/investor/research-reports/${id}`);
  },

  // ==================== Watchlist ====================
  
  /**
   * Get watchlist
   */
  async getWatchlist() {
    return api.get('/investor/watchlist');
  },

  /**
   * Add to watchlist
   */
  async addToWatchlist(data) {
    return api.post('/investor/watchlist', data);
  },

  /**
   * Remove from watchlist
   */
  async removeFromWatchlist(id) {
    return api.delete(`/investor/watchlist/${id}`);
  },

  /**
   * Get investor onboarding details by email
   */
  async getInvestorOnboardingDetailsByEmail(email) {
    const params = new URLSearchParams({ email });
    return api.get(`/investor/onboarding/details/email?${params.toString()}`);
  },

  /**
   * Get investor onboarding details by user ID
   */
  async getInvestorOnboardingDetailsByUserId(userId) {
    const params = new URLSearchParams({ userId });
    return api.get(`/investor/onboarding/details/userid?${params.toString()}`);
  },

  async getPortfolioPerformance(userId,status='ACTIVE') {
    const params = new URLSearchParams({status });
    return api.get(`/investor/portfolio/performance/${userId}?${params.toString()}`);
  }
}
export default investorService;
